import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { addProcedure, fetchSpecialtiesList } from "../../Adapters/ApiAdapter";
import { baseUrl } from "../../variables/Variables";
import CKEditorField from "../../components/CKEditorField";

const AddProcedure = () => {
  const [loading, setLoading] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    overview: "",
    status: "active",
    speciality_id: "",
    display_order: "0",
    seo_title: "",
    meta_title: "",
    meta_description: "",
    meta_keywords: "",
    custom_scripts: "",
    og_title: "",
    og_description: "",
    og_type: "",
    og_url: "",
    og_locale: "",
    og_site_name: "",
    og_image_width: "",
    og_image_height: "",
    og_image_type: "",
    article_modified_time: "",
    canonical_link: "",
    twitter_card: "",
    twitter_title: "",
    twitter_description: "",
    twitter_site: "",
  });

  const [imgFile, setImgFile] = useState(null);
  const [bannerImgFile, setBannerImgFile] = useState(null);
  const [ogImgFile, setOgImgFile] = useState(null);
  const [twitterImgFile, setTwitterImgFile] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    const loadSpecialties = async () => {
      try {
        const response = await fetchSpecialtiesList();
        if (response && response.specialities) {
          setSpecialties(response.specialities);
        }
      } catch (error) {
        toast.error("Failed to load specialties.");
      }
    };
    loadSpecialties();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "name") {
        updated.slug = value.toLowerCase().replace(/ /g, "-").replace(/[^\w-]+/g, "");
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const data = new FormData();
    Object.keys(formData).forEach((key) => {
      data.append(key, formData[key]);
    });

    if (imgFile) data.append("img_path", imgFile);
    if (bannerImgFile) data.append("banner_img", bannerImgFile);
    if (ogImgFile) data.append("og_image", ogImgFile);
    if (twitterImgFile) data.append("twitter_image", twitterImgFile);

    try {
      const response = await addProcedure(data);
      if (response.status === "true") {
        toast.success("Procedure added successfully!");
        setTimeout(() => navigate("/list-procedures"), 2000);
      } else {
        toast.error(response.message || "Failed to add procedure.");
      }
    } catch (error) {
      toast.error("Error adding procedure.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box m="20px">
      <ToastContainer />
      <Typography variant="h4" gutterBottom>
        Add Procedure
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          margin="normal"
          label="Procedure Name"
          name="name"
          value={formData.name}
          onChange={handleInputChange}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Slug"
          name="slug"
          value={formData.slug}
          onChange={handleInputChange}
          required
        />

        <FormControl fullWidth margin="normal" required>
          <InputLabel>Mapped Speciality</InputLabel>
          <Select
            name="speciality_id"
            value={formData.speciality_id}
            onChange={handleInputChange}
            label="Mapped Speciality"
          >
            {specialties.map((spec) => (
              <MenuItem key={spec.id} value={spec.id}>
                {spec.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl fullWidth margin="normal">
          <InputLabel>Status</InputLabel>
          <Select
            name="status"
            value={formData.status}
            onChange={handleInputChange}
            label="Status"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </Select>
        </FormControl>


        <TextField
          fullWidth
          margin="normal"
          label="Display Order"
          name="display_order"
          type="number"
          value={formData.display_order}
          onChange={handleInputChange}
        />

        <Typography variant="body1">Overview Content</Typography>
        <CKEditorField
          value={formData.overview}
          onChange={(data) => setFormData((prev) => ({ ...prev, overview: data }))}
          uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
        />

        <TextField
          fullWidth
          margin="normal"
          label="SEO Title"
          name="seo_title"
          value={formData.seo_title}
          onChange={handleInputChange}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Meta Title"
          name="meta_title"
          value={formData.meta_title}
          onChange={handleInputChange}
        />

        <Typography variant="body1">Meta Description</Typography>
        <CKEditorField
          value={formData.meta_description}
          onChange={(data) => setFormData((prev) => ({ ...prev, meta_description: data }))}
          uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Meta Keywords"
          name="meta_keywords"
          value={formData.meta_keywords}
          onChange={handleInputChange}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Canonical Link"
          name="canonical_link"
          value={formData.canonical_link}
          onChange={handleInputChange}
        />

        <Typography variant="body1">Custom Scripts</Typography>
        <CKEditorField
          value={formData.custom_scripts}
          onChange={(data) => setFormData((prev) => ({ ...prev, custom_scripts: data }))}
          uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
        />

        <TextField
          fullWidth
          margin="normal"
          label="OG Title"
          name="og_title"
          value={formData.og_title}
          onChange={handleInputChange}
        />

        <Typography variant="body1">OG Description</Typography>
        <CKEditorField
          value={formData.og_description}
          onChange={(data) => setFormData((prev) => ({ ...prev, og_description: data }))}
          uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
        />

        <TextField
          fullWidth
          margin="normal"
          label="OG Type"
          name="og_type"
          value={formData.og_type}
          onChange={handleInputChange}
        />

        <TextField
          fullWidth
          margin="normal"
          label="OG URL"
          name="og_url"
          value={formData.og_url}
          onChange={handleInputChange}
        />

        <TextField
          fullWidth
          margin="normal"
          label="Twitter Title"
          name="twitter_title"
          value={formData.twitter_title}
          onChange={handleInputChange}
        />

        <Typography variant="body1">Twitter Description</Typography>
        <CKEditorField
          value={formData.twitter_description}
          onChange={(data) => setFormData((prev) => ({ ...prev, twitter_description: data }))}
          uploadUrl={`${baseUrl}api/ck-editor-upload?folder=procedure`}
        />

        <Box mt={3}>
          <Button variant="contained" component="label">
            Upload Thumbnail Image
            <input type="file" accept="image/*" hidden onChange={(e) => setImgFile(e.target.files[0])} />
          </Button>
          {imgFile && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
              Selected File: {imgFile.name}
            </Typography>
          )}
          <br />
          <Typography variant="caption" sx={{ color: 'red' }}>
            Procedure Image should be 60x60px
          </Typography>
        </Box>

        <Box mt={3}>
          <Button variant="contained" component="label">
            Upload Banner Image
            <input type="file" accept="image/*" hidden onChange={(e) => setBannerImgFile(e.target.files[0])} />
          </Button>
          {bannerImgFile && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
              Selected File: {bannerImgFile.name}
            </Typography>
          )}
          <br />
          <Typography variant="caption" sx={{ color: 'red' }}>
            Banner Image should be 1380x260px
          </Typography>
        </Box>

        <Box mt={3}>
          <Button variant="contained" component="label">
            Upload OG Image
            <input type="file" accept="image/*" hidden onChange={(e) => setOgImgFile(e.target.files[0])} />
          </Button>
          {ogImgFile && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
              Selected File: {ogImgFile.name}
            </Typography>
          )}
        </Box>

        <Box mt={3}>
          <Button variant="contained" component="label">
            Upload Twitter Image
            <input type="file" accept="image/*" hidden onChange={(e) => setTwitterImgFile(e.target.files[0])} />
          </Button>
          {twitterImgFile && (
            <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
              Selected File: {twitterImgFile.name}
            </Typography>
          )}
        </Box>

        <Box mt={4} mb={4}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            style={{ background: "#0ca854" }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Add Procedure"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AddProcedure;
